// src/providers/GeoContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const GEO_CODES = ["kw", "ae", "qa", "bh", "om", "ph", "my"];
const DEFAULT_GEO = "my";

const GeoCtx = createContext({
  geo: DEFAULT_GEO,
  setGeo: () => {},
  navigateToGeo: () => {},
  geoPath: () => "",
  GEO_CODES,
});

export const useGeo = () => useContext(GeoCtx);

/** Wraps a route segment like "/:geo/*" and keeps geo in context */
export function GeoLayoutProvider({ children }) {
  const { geo: paramGeo } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [geo, setGeo] = useState(DEFAULT_GEO);

  // Validate the ":geo" param and persist it
  useEffect(() => {
    const g = (paramGeo || "").toLowerCase();
    if (!GEO_CODES.includes(g)) {
      // fallback: saved localStorage or default
      const saved = (localStorage.getItem("geo") || "").toLowerCase();
      const fallback = GEO_CODES.includes(saved) ? saved : DEFAULT_GEO;
      const rest = location.pathname.replace(/^\/[^/]+/, ""); // strip the first segment
      navigate(`/${fallback}${rest}${location.search}${location.hash}`, { replace: true });
      return;
    }
    setGeo(g);
    localStorage.setItem("geo", g);
  }, [paramGeo, location.pathname, location.search, location.hash, navigate]);

  // Build a path under the current geo (accepts "faq" or "/faq")
  const geoPath = useCallback(
    (path = "") => {
      const clean = `${path}`.startsWith("/") ? path : `/${path}`;
      return `/${geo}${clean}`;
    },
    [geo]
  );

  // Switch geo but keep the current page
  const navigateToGeo = useCallback(
    (newGeo) => {
      const g = (newGeo || "").toLowerCase();
      if (!GEO_CODES.includes(g)) return;
      const rest = location.pathname.replace(/^\/[^/]+/, ""); // remove current geo segment
      navigate(`/${g}${rest}${location.search}${location.hash}`);
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  const value = useMemo(() => ({ geo, setGeo, navigateToGeo, geoPath, GEO_CODES }), [geo, navigateToGeo, geoPath]);

  return <GeoCtx.Provider value={value}>{children}</GeoCtx.Provider>;
}
